import { ProBadge } from "@/components/ui/ProBadge";
import { Check, ShieldCheck } from "lucide-react";
import type { ColorPalette } from "@profolio/templates/types";

export interface ColorSwatchGridProps {
  palettes: ColorPalette[];
  selectedId: string;
  onSelect: (id: string) => void;
  isProUser: boolean;
  isPalettePro: (id: string) => boolean;
  onUpgradeClick?: (featureKey?: string) => void;
  className?: string;
}

export function ColorSwatchGrid({
  palettes,
  selectedId,
  onSelect,
  isProUser,
  isPalettePro,
  onUpgradeClick,
  className = "",
}: ColorSwatchGridProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Color Palettes"
      className={`grid grid-cols-2 sm:grid-cols-4 gap-3 ${className}`}
    >
      {palettes.map((palette) => {
        const isSelected = selectedId === palette.id;
        const locked = isPalettePro(palette.id) && !isProUser;

        const handleClick = () => {
          if (locked && onUpgradeClick) {
            onUpgradeClick("premium_palette");
            return;
          }
          onSelect(palette.id);
        };

        return (
          <button
            key={palette.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={handleClick}
            className={`p-3 rounded-xl border text-left transition-all outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-primary ${
              isSelected
                ? "border-primary ring-1 ring-primary bg-primary/5 shadow-xs"
                : "border-border bg-card hover:border-primary/40 hover:bg-muted/40 shadow-2xs"
            }`}
          >
            {/* Color Swatch Bar */}
            <div className="flex h-6 w-full rounded-lg overflow-hidden mb-2.5 border border-black/10 shadow-2xs">
              <div
                className="flex-1"
                style={{ backgroundColor: palette.background }}
                title={`Background: ${palette.background}`}
              />
              <div
                className="flex-1"
                style={{ backgroundColor: palette.surfaceElevated || palette.muted }}
                title="Surface"
              />
              <div
                className="flex-1"
                style={{ backgroundColor: palette.primary }}
                title={`Primary: ${palette.primary}`}
              />
              <div
                className="flex-1"
                style={{ backgroundColor: palette.accent }}
                title={`Accent: ${palette.accent}`}
              />
            </div>

            <div className="flex items-center justify-between mb-1">
              <span className="text-xs sm:text-sm font-semibold truncate capitalize text-foreground">
                {palette.name}
              </span>
              {locked ? (
                <ProBadge locked />
              ) : isSelected ? (
                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0">
                  <Check className="h-2.5 w-2.5" />
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-medium ${
                  palette.isDark
                    ? "bg-slate-900 text-slate-200 dark:bg-slate-800"
                    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                }`}
              >
                {palette.isDark ? "Dark" : "Light"}
              </span>
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-0.5">
                <ShieldCheck className="h-3 w-3" /> AA
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
